import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react";
import type { StyleProp, ViewStyle } from "react-native";

/* -----------------Third parties--------------- */
import {
	BottomSheetBackdrop,
	type BottomSheetBackdropProps,
	BottomSheetModal,
	type BottomSheetModalProps,
	BottomSheetScrollView,
	type BottomSheetScrollViewMethods,
} from "@gorhom/bottom-sheet";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useAppTheme } from "../../useAppTheme";
/* -----------------Hooks--------------- */
import Box from "../Box";
import SheetHandle from "../SheetHandle";

export type CustomHandle = (
	handleExpandCollapse: () => void,
) => React.ReactNode;

type Props = Omit<
	BottomSheetModalProps,
	"handleComponent" | "snapPoints" | "index"
> & {
	children: React.ReactNode;
	showModal?: boolean;
	snapIndex: number;
	handleSetSnapIndex?: (index: number) => void;
	customHandle?: CustomHandle;
	header?: React.ReactNode;
	backdropPressBehavior?: React.ComponentProps<
		typeof BottomSheetBackdrop
	>["pressBehavior"];
	backdropOpacity?: number;
	backdropAppearsOnIndex?: number;
	backdropDisappearsOnIndex?: number;
	contentContainerStyle?: StyleProp<ViewStyle>;
} & (
		| {
				snapPoint: number | string;
				snapPoints?: never;
		  }
		| {
				snapPoints: (number | string)[];
				snapPoint?: never;
		  }
	);

const BottomSheetModalWrapper = forwardRef<BottomSheetModal, Props>(
	(props, ref) => {
		const {
			children,
			showModal,
			snapPoint,
			snapPoints: snapPointsProp,
			handleSetSnapIndex,
			customHandle: CustomHandle,
			animateOnMount = false,
			enableDismissOnClose = false,
			enablePanDownToClose = false,
			snapIndex = 0,
			style,
			backgroundStyle,
			onChange,
			header,
			backdropPressBehavior = "close",
			backdropOpacity,
			backdropAppearsOnIndex = 1,
			backdropDisappearsOnIndex = 0,
			contentContainerStyle,
			bottomInset = 0,
			...rest
		} = props;

		const theme = useAppTheme();
		const insets = useSafeAreaInsets();
		const scrollViewRef = useRef<BottomSheetScrollViewMethods>(null);
		const currentSnapIndexRef = useRef(snapIndex);
		const snapPoints = useMemo(
			() => snapPointsProp || [snapPoint, "90%"],
			[snapPoint, snapPointsProp],
		);

		const renderBackdrop = useCallback(
			(backdropProps: BottomSheetBackdropProps) => (
				<BottomSheetBackdrop
					{...backdropProps}
					style={[
						backdropProps.style,
						bottomInset ? { bottom: bottomInset } : undefined,
					]}
					opacity={backdropOpacity}
					appearsOnIndex={backdropAppearsOnIndex}
					disappearsOnIndex={backdropDisappearsOnIndex}
					pressBehavior={backdropPressBehavior}
				/>
			),
			[
				backdropAppearsOnIndex,
				backdropDisappearsOnIndex,
				backdropOpacity,
				backdropPressBehavior,
				bottomInset,
			],
		);

		const innerBottomSheetRef = useRef<BottomSheetModal>(null);
		useImperativeHandle(ref, () => innerBottomSheetRef.current!, []);

		const handleSheetChange = useCallback(
			(index: number) => {
				handleSetSnapIndex?.(index);
				if (index === 0) {
					scrollViewRef.current?.scrollTo({ x: 0, y: 0, animated: true });
				}
				currentSnapIndexRef.current = index;
				onChange?.(index, 0, 0);
			},
			[handleSetSnapIndex, onChange],
		);

		useEffect(() => {
			if (showModal) {
				innerBottomSheetRef.current?.present();
			} else {
				innerBottomSheetRef.current?.dismiss();
			}
		}, [showModal]);

		useEffect(() => {
			return () => {
				innerBottomSheetRef.current?.dismiss();
			};
		}, []);

		const handleHandlePress = useCallback(() => {
			try {
				if (innerBottomSheetRef.current) {
					if (currentSnapIndexRef.current === 0) {
						innerBottomSheetRef.current.snapToIndex(1);
					} else {
						innerBottomSheetRef.current.snapToIndex(0);
					}
				}
			} catch (_e) {
				// This means that there is only 1 snap point
				innerBottomSheetRef.current?.dismiss();
			}
		}, []);

		const customHandleRef = useRef(CustomHandle);
		customHandleRef.current = CustomHandle;

		const Handle = useCallback(() => {
			if (customHandleRef.current) {
				return customHandleRef.current(handleHandlePress);
			}
			return <SheetHandle onPress={handleHandlePress} />;
		}, [handleHandlePress]);

		if (!showModal) {
			return null;
		}

		return (
			<BottomSheetModal
				ref={innerBottomSheetRef}
				style={[
					{
						shadowColor: theme.colors.shadow,
						shadowOffset: {
							width: 0,
							height: 0,
						},
						shadowOpacity: 1,
						shadowRadius: 8,
						elevation: 8,
						borderTopLeftRadius: 25,
						borderTopRightRadius: 25,
					},
					style,
				]}
				backgroundStyle={[
					{
						borderStyle: "solid",
						borderWidth: 2,
						borderColor: theme.palette.divider,
						borderTopLeftRadius: 25,
						borderTopRightRadius: 25,
						backgroundColor: theme.colors.elevation.level1,
					},
					backgroundStyle,
				]}
				animateOnMount={animateOnMount}
				enableDismissOnClose={enableDismissOnClose}
				enablePanDownToClose={enablePanDownToClose}
				index={snapIndex}
				snapPoints={snapPoints}
				enableDynamicSizing={false}
				topInset={insets.top + 10}
				bottomInset={bottomInset}
				onChange={handleSheetChange}
				handleComponent={Handle}
				backdropComponent={renderBackdrop}
				{...rest}
			>
				{!!header && <Box paddingHorizontal={16}>{header}</Box>}
				<BottomSheetScrollView
					contentContainerStyle={[
						{
							padding: 16,
							paddingTop: 0,
							paddingBottom: 80,
							flexGrow: 1,
						},
						contentContainerStyle,
					]}
					ref={scrollViewRef}
				>
					{children}
				</BottomSheetScrollView>
			</BottomSheetModal>
		);
	},
);
export default BottomSheetModalWrapper;
